import { Component, OnInit, ViewEncapsulation, Input, Output, EventEmitter } from '@angular/core';
import { HierarchyWebModel, ExtendedAccessModel } from '../../../interfaces/hierarchy-data';
import {
    NoDataTypes,
    NoDataSourceType,
    NoDataActionType,
} from '../../../../shared/interfaces/no-data';
import { User } from '../../../../shared/services/user.service';
import { Employee, ClientCustomRole } from '../../../../shared/interfaces/client-data';

// Services
import { UserExtendedAccessService } from '../../../services/user-extended-access.service';
import { HierarchyService } from '../../../services/hierarchy.service';
import { ClientsService } from '../../../../shared/services/clients.service';

@Component({
    selector: 'app-user-extended-access-menu',
    templateUrl: './user-extended-access-menu.component.html',
    styleUrls: ['./user-extended-access-menu.component.scss'],
})
export class UserExtendedAccessMenuComponent implements OnInit {
    userExtendedAccess: any[];
    userExtendedAccessUpdate: any[];
    userExtendedAccessOriginal: any[];
    noDataType: NoDataTypes;
    extendedAccessLoaded: boolean;
    errors: string = null;
    clientCustomRoles: ClientCustomRole[];
    levelEntities: any[];

    employeeListSettings: any;
    entityListSettings: any;
    roleListSettings: any;
    defaultRoleOrder: number;

    @Input() user: User;
    @Input() employees: Employee[];
    @Input() clientShortName: string;

    @Output() cancel = new EventEmitter<string>();
    @Output() saved = new EventEmitter();

    constructor(
        private hierarchyService: HierarchyService,
        private userExtendedAccessService: UserExtendedAccessService,
        private clientService: ClientsService
    ) {}

    ngOnInit() {
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'extended access',
            inputSource: NoDataSourceType.table,
        };

        this.employeeListSettings = {
            singleSelection: true,
            text: 'All',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'lfName',
            primaryKey: 'primaryIdentifier',
            enableFilterSelectAll: false,
        };

        this.entityListSettings = {
            singleSelection: true,
            text: 'All',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'hierarchyName',
            primaryKey: 'hierarchyIdentifier',
            enableFilterSelectAll: false,
        };

        this.roleListSettings = {
            singleSelection: true,
            text: 'Select',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'roleName',
            primaryKey: 'roleOrder',
            enableFilterSelectAll: false,
        };

        const tempEmployees = this.employees.filter(
            (e) => e.employeeStatus != 'Terminated' && e.loginEmail != this.user.loginEmail
        );
        this.employees = JSON.parse(JSON.stringify(tempEmployees));

        this.getAccessLevelsForClient();
    }

    //

    getExtendedAccess() {
        this.extendedAccessLoaded = false;

        try {
            this.userExtendedAccessService.get(this.user.loginEmail).subscribe(
                (data) => {
                    this.userExtendedAccess = JSON.parse(JSON.stringify(data));

                    // Add role object for drop down
                    // Defaults to branch for existing access
                    this.userExtendedAccess.forEach((x) => {
                        if (!x.roleOrder) {
                            x.roleOrder = [this.clientCustomRoles.find((y) => y.roleOrder == 2)];
                        } else {
                            x.roleOrder = [
                                this.clientCustomRoles.find((y) => y.roleOrder == x.roleOrder),
                            ];
                        }
                    });

                    // Remove items greater than the current user's hierarchy level (Per MGB)
                    this.userExtendedAccess = this.userExtendedAccess.filter(
                        (x) => x.roleOrder[0] && x.roleOrder[0].roleOrder <= this.defaultRoleOrder
                    );

                    // Set values compatable with angular2-multiselect
                    this.userExtendedAccess.forEach((x) => {
                        x.entities = this.levelEntities.find(
                            (y) => y.roleOrder == x.roleOrder[0].roleOrder
                        ).entities;

                        if (x.hierarchyIdentifier == '0') {
                            x.hierarchyIdentifier = null;
                        } else {
                            x.hierarchyIdentifier = [
                                x.entities.find(
                                    (y) => y.hierarchyIdentifier == x.hierarchyIdentifier
                                ),
                            ];
                        }

                        if (x.employee == '0') {
                            x.employee = null;
                        } else {
                            x.employee = [
                                this.employees.find((y) => y.primaryIdentifier == x.employee),
                            ];
                        }
                    });

                    // Create copy of extended access array for later reference in update logic
                    this.userExtendedAccessOriginal = JSON.parse(
                        JSON.stringify(this.userExtendedAccess)
                    );

                    this.extendedAccessLoaded = true;
                },
                (error) => {
                    throw error;
                }
            );
        } catch (e) {
            this.extendedAccessLoaded = true;
            this.cancel.emit('Something went wrong fetching extended access. Try again later.');
            throw new Error(e);
        }
    }

    getAccessLevelsForClient() {
        this.clientService.getCustomRoles().subscribe(
            (roles) => {
                if (!roles) {
                    throw new Error('Client has not yet setup their custom role levels.');
                } else {
                    // Filter out roles higher than the current user's hierarchy level
                    const userRole = roles.find(
                        (r) => r.roleName == this.user.clientCustomRoleName
                    );
                    this.defaultRoleOrder = userRole.roleOrder;
                    this.clientCustomRoles = roles.filter(
                        (r) =>
                            r.roleOrder <= userRole.roleOrder &&
                            r.roleOrder != 1 &&
                            r.roleOrder != 10
                    );

                    // Fetch hierarchy Entities
                    this.getHierarchyEntities();
                }
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    getHierarchyEntities() {
        this.levelEntities = [];

        this.clientCustomRoles.forEach((x) => {
            this.hierarchyService.getEntitiesByRoleOrder(x.roleOrder).subscribe(
                (entities) => {
                    if (entities) {
                        const model = {
                            roleOrder: x.roleOrder,
                            entities: entities,
                        };
                        this.levelEntities.push(model);
                    }
                },
                (error) => {
                    throw new Error(error);
                }
            );
        });

        const numRoles = this.clientCustomRoles.length;
        let numLevels = this.levelEntities.length;

        let _this = this;

        // Need to pause logic to wait for all hierarchy levels to be loaded
        if (numRoles != numLevels) {
            setTimeout(function () {
                _this.getExtendedAccess();
            }, 1000);
        } else {
            this.getExtendedAccess();
        }
    }

    updateExtendedAccess(model?: any, isDeleted?: boolean) {
        try {
            if (!this.userExtendedAccessUpdate) {
                this.userExtendedAccessUpdate = [];
            }

            if (model) {
                if (isDeleted && isDeleted === true) {
                    model.isDeleted = true;
                    const indexOfExistingOriginal = this.userExtendedAccessOriginal.findIndex(
                        (x) => x.extendedAccessKey === model.extendedAccessKey
                    );

                    if (indexOfExistingOriginal < 0) {
                        // Model is new, and was not here before edit:
                        const indexOfExisting = this.userExtendedAccess.findIndex(
                            (x) => x.extendedAccessKey === model.extendedAccessKey
                        );
                        if (indexOfExisting >= 0) {
                            this.userExtendedAccess.splice(indexOfExisting, 1);
                        }

                        this.removeModelFromUpdateIfExists(model);
                    } else {
                        // Model is not new, and has since been deleted:
                        this.removeModelFromUpdateIfExists(model);
                        this.userExtendedAccessUpdate.push(model);
                    }
                } else {
                    model.isDeleted = false;
                    this.removeModelFromUpdateIfExists(model);
                }

                // Regular update logic
                const originalModel = this.userExtendedAccessOriginal.find(
                    (x) => x.extendedAccessKey === model.extendedAccessKey
                );

                if (originalModel && originalModel.isDeleted === model.isDeleted) {
                    this.removeModelFromUpdateIfExists(model);

                    if (
                        originalModel.employee !== model.employee ||
                        originalModel.hierarchyIdentifier !== model.hierarchyIdentifier ||
                        originalModel.roleOrder[0].roleOrder !== model.roleOrder[0].roleOrder
                    ) {
                        if (model.roleOrder[0].roleOrder !== originalModel.roleOrder[0].roleOrder) {
                            model.entities = this.levelEntities.find(
                                (x) => x.roleOrder == model.roleOrder[0].roleOrder
                            ).entities;

                            if (
                                model.hierarchyIdentifier &&
                                model.hierarchyIdentifier.length >= 1
                            ) {
                                if (
                                    !model.entities.find(
                                        (x) =>
                                            x.hierarchyIdentifier ===
                                            model.hierarchyIdentifier[0].hierarchyIdentifier
                                    )
                                ) {
                                    model.hierarchyIdentifier = null;
                                }
                            }
                        }

                        this.userExtendedAccessUpdate.push(model);
                    }
                } else {
                    if (!isDeleted) {
                        this.removeModelFromUpdateIfExists(model);

                        model.entities = this.levelEntities.find(
                            (x) => x.roleOrder == model.roleOrder[0].roleOrder
                        ).entities;

                        if (model.hierarchyIdentifier && model.hierarchyIdentifier.length >= 1) {
                            if (
                                !model.entities.find(
                                    (x) =>
                                        x.hierarchyIdentifier ===
                                        model.hierarchyIdentifier[0].hierarchyIdentifier
                                )
                            ) {
                                model.hierarchyIdentifier = null;
                            }
                        }

                        this.userExtendedAccessUpdate.push(model);
                    }
                }
            } else {
                // Add new template model
                const newModel = {
                    extendedAccessKey: this.userExtendedAccess.length + 1,
                    isDeleted: false,
                    roleOrder: [
                        this.clientCustomRoles.find((y) => y.roleOrder == this.defaultRoleOrder),
                    ],
                    hierarchyIdentifier: null,
                    employee: null,
                    entities: this.levelEntities.find((y) => y.roleOrder == this.defaultRoleOrder)
                        .entities,
                };

                this.userExtendedAccess.push(newModel);
                this.userExtendedAccessUpdate.push(newModel);
            }

            let hasInvalid = this.userExtendedAccess.find(
                (x) =>
                    (x.employee == null || x.employee.length == 0) &&
                    (x.hierarchyIdentifier == null || x.hierarchyIdentifier.length == 0)
            );
            if (hasInvalid) {
                this.errors = 'You must select at least one hierarchy entity or one employee.';
            } else {
                this.errors = null;
            }
        } catch (e) {
            this.cancel.emit('Something went wrong. Try again later.');
            throw new Error(e);
        }
    }

    removeModelFromUpdateIfExists(model: ExtendedAccessModel) {
        const indexOfExistingUpdate = this.userExtendedAccessUpdate.findIndex(
            (x) => x.extendedAccessKey === model.extendedAccessKey
        );
        if (indexOfExistingUpdate >= 0) {
            this.userExtendedAccessUpdate.splice(indexOfExistingUpdate, 1);
        }
    }

    canSaveExtendedAccess() {
        if (this.errors) {
            return false;
        }

        return this.userExtendedAccessUpdate && this.userExtendedAccessUpdate.length > 0;
    }

    saveExtendedAccess() {
        if (this.canSaveExtendedAccess()) {
            try {
                let updateData = [];

                this.userExtendedAccessUpdate.forEach((x) => {
                    let updateModel = {
                        roleOrder: x.roleOrder[0].roleOrder,
                        employee: null,
                        hierarchyIdentifier: null,
                        loginEmail: this.user.loginEmail,
                        clientUserAccessKey: x.clientUserAccessKey,
                        hierarchyManagementKey: x.hierarchyManagementKey,
                        isDeleted: x.isDeleted,
                    };

                    if (x.employee && x.employee.length >= 1) {
                        updateModel.employee = x.employee[0].primaryIdentifier;
                    } else {
                        updateModel.employee = '0';
                    }

                    if (x.hierarchyIdentifier && x.hierarchyIdentifier.length >= 1) {
                        updateModel.hierarchyIdentifier =
                            x.hierarchyIdentifier[0].hierarchyIdentifier;
                    } else {
                        updateModel.hierarchyIdentifier = '0';
                    }

                    updateData.push(updateModel);
                });

                this.userExtendedAccessService.save(updateData).subscribe(
                    (data) => {
                        this.saved.emit();
                    },
                    (error) => {
                        throw error;
                    }
                );
            } catch (e) {
                this.cancel.emit('Something went wrong saving extended access. Try again later.');
                throw new Error(e);
            }
        }
    }

    cancelMenu() {
        this.cancel.emit();
    }
}
